import {
  clearAccess,
  currentAudience,
  invitationAccessHint,
  readAccess,
  saveAccess,
} from './accessState';
import type { AccessSessionResponse } from '@biasafe.models';
import type { AccessAudience } from './accessState';
import apiClient from '@/api';

export async function exchangeAccessSession(
  idToken: string,
  audience: AccessAudience = currentAudience(),
): Promise<AccessSessionResponse> {
  const { data } = await apiClient.post<AccessSessionResponse>(
    '/auth/gcp/session',
    {
      id_token: idToken,
      audience,
      access_id: invitationAccessHint() ?? readAccess(audience)?.choice.id,
    },
  );
  saveAccess(data, audience);
  return data;
}

export async function listAccesses(): Promise<AccessSessionResponse> {
  const { data } = await apiClient.get<AccessSessionResponse>('/auth/accesses');
  return data;
}

export async function selectAccess(
  accessId: string,
): Promise<AccessSessionResponse> {
  const { data } = await apiClient.post<AccessSessionResponse>(
    '/auth/accesses/select',
    {
      access_id: accessId,
    },
  );
  saveAccess(data);
  return data;
}

// New tabs share the audience cookie, but must choose their own membership.
// No profile/portfolio request runs until that tab has an explicit selection.
export async function ensureAccess(): Promise<boolean> {
  const selected = readAccess();
  const requested = invitationAccessHint();
  if (selected && (!requested || selected.choice.id === requested)) return true;
  let result: AccessSessionResponse;
  try {
    result = await listAccesses();
  } catch (error) {
    if ((error as { response?: { status?: number } }).response?.status !== 401)
      throw error;
    const { getCurrentIdToken } = await import('./gcp/gcpAuth');
    const token = await getCurrentIdToken(true);
    if (!token) throw error;
    result = await exchangeAccessSession(token);
    if (result.selectedAccess) return true;
  }
  const matches = result.accesses.filter(
    (c) => c.audience === currentAudience(),
  );
  const hint = invitationAccessHint();
  const choice = hint
    ? matches.find((c) => c.id === hint)
    : matches.length === 1
      ? matches[0]
      : undefined;
  if (!choice) return false;
  await selectAccess(choice.id);
  return true;
}

export async function endAccessSession(): Promise<void> {
  const audience = currentAudience();
  clearAccess(audience);
  // Only this audience's provider app and server cookie are signed out.
  // Neither an advisor sign-out nor a client sign-out changes the other one.
  const provider = import('./gcp/gcpAuth')
    .then((gcp) => gcp.signOutGcp())
    .catch(() => undefined);
  try {
    await apiClient.post('/auth/logout', undefined, { timeout: 5000 });
  } finally {
    await provider;
  }
}
